import { TourInfo, TransportationInfo } from "@/components/quotation/types";
import { normalizeCoordinates } from "./coordinates-utils";

// Constante para a chave do localStorage
const DRAFT_KEY = 'voah_quotation_draft';

/**
 * Verifica o estado atual das coordenadas dos tours persistidos no localStorage
 */
export function monitorStoredTourCoordinates() {
  try {
    // Obtém as cotações armazenadas
    const storedData = localStorage.getItem(DRAFT_KEY);
    if (!storedData) {
      return;
    }
    
    const parsedData = JSON.parse(storedData);
    
    if (!parsedData || !parsedData.pages) {
      return;
    }
    
    // Busca todos os tours em todas as páginas
    let allTours: any[] = [];
    parsedData.pages.forEach((page: any) => {
      if (page.tours && Array.isArray(page.tours)) {
        allTours.push(...page.tours);
      }
    });
    
    return allTours;
  } catch (error) {
    return [];
  }
}

/**
 * Força a persistência segura de coordenadas para um tour específico
 */
export function forceUpdateTourCoordinates(tourId: string, coordinates: {lat: number|string, lng: number|string} | null) {
  try {
    // Se as coordenadas são nulas, não há o que atualizar
    if (!coordinates) {
      return false;
    }
    
    // Obter as cotações armazenadas
    const storedData = localStorage.getItem(DRAFT_KEY);
    if (!storedData) {
      return false;
    }
    
    const parsedData = JSON.parse(storedData);
    
    if (!parsedData || !parsedData.pages) {
      return false;
    }
    
    // Busca o tour em todas as páginas
    let updated = false;
    
    // Garantir que as coordenadas sejam números
    // Usar nossa própria lógica de normalização em vez de depender da função externa
    let safeCoordinates;
    
    try {
      // Converter para string primeiro para garantir formato consistente
      const latStr = String(coordinates.lat).replace(',', '.');
      const lngStr = String(coordinates.lng).replace(',', '.');
      
      // Converter para números
      const lat = Number(latStr);
      const lng = Number(lngStr);
      
      // Verificar se são números válidos
      if (isNaN(lat) || isNaN(lng)) {
        return false;
      }
      
      // Criar objeto seguro com valores numéricos
      safeCoordinates = {
        lat: lat,
        lng: lng
      };
    } catch (err) {
      return false;
    }
    
    // Atualizar todas as páginas com as coordenadas seguras
    parsedData.pages.forEach((page: any) => {
      if (page.tours && Array.isArray(page.tours)) {
        page.tours.forEach((tour: any) => {
          if (tour.id === tourId) {
            // Atualiza as coordenadas diretamente
            tour.coordinates = safeCoordinates;
            updated = true;
          }
        });
      }
    });
    
    if (updated) {
      // Salva a cotação atualizada no localStorage
      localStorage.setItem(DRAFT_KEY, JSON.stringify(parsedData));
      
      // Verificar se a atualização foi aplicada
      monitorStoredTourCoordinates();
      return true;
    } else {
      return false;
    }
  } catch (error) {
    return false;
  }
}

/**
 * Utility function to log tour coordinate state for debugging
 */
export function logTourCoordinatesState(tour: TourInfo, stage: string) {
  // Função mantida para compatibilidade, mas sem logs
}

/**
 * Debug helper to check tour coordinates before saving to localStorage
 */
export function debugBeforeSave(tours: TourInfo[]) {
  // Função mantida para compatibilidade, mas sem logs
}

/**
 * Debug helper to check tour coordinates after loading from localStorage
 */
export function debugAfterLoad(tours: TourInfo[]) {
  // Função mantida para compatibilidade, mas sem logs
}

/**
 * Força a persistência segura de coordenadas para um traslado específico
 */
export function forceUpdateTransportationCoordinates(transportationId: string, coordinates: {lat: number|string, lng: number|string}) {
  try {
    // Obtém as cotações armazenadas
    const storedData = localStorage.getItem(DRAFT_KEY);
    if (!storedData) {
      return false;
    }
    
    const parsedData = JSON.parse(storedData);
    
    if (!parsedData || !parsedData.pages) {
      return false;
    }
    
    // Busca o traslado em todas as páginas
    let updated = false;
    
    // Normalize coordinates for consistency across platforms
    const normalizedCoords = normalizeCoordinates(coordinates);
    
    if (!normalizedCoords) {
      return false;
    }
    
    // Cria cópia segura das coordenadas normalizadas
    const safeCoordinates = {
      lat: normalizedCoords.lat,
      lng: normalizedCoords.lng
    };
    
    parsedData.pages.forEach((page: any) => {
      if (page.transportations && Array.isArray(page.transportations)) {
        page.transportations.forEach((transportation: any) => {
          if (transportation.id === transportationId) {
            // Atualiza as coordenadas diretamente
            transportation.coordinates = safeCoordinates;
            updated = true;
          }
        });
      }
    });
    
    if (updated) {
      // Salva a cotação atualizada no localStorage
      localStorage.setItem(DRAFT_KEY, JSON.stringify(parsedData));
      return true;
    } else {
      return false;
    }
  } catch (error) {
    return false;
  }
}

/**
 * Verifica o estado atual das coordenadas dos traslados persistidos no localStorage
 */
export function monitorStoredTransportationCoordinates() {
  try {
    // Obtém as cotações armazenadas
    const storedData = localStorage.getItem(DRAFT_KEY);
    if (!storedData) {
      return;
    }
    
    const parsedData = JSON.parse(storedData);
    
    if (!parsedData || !parsedData.pages) {
      return;
    }
    
    // Busca todos os traslados em todas as páginas
    let allTransportations: any[] = [];
    parsedData.pages.forEach((page: any) => {
      if (page.transportations && Array.isArray(page.transportations)) {
        allTransportations.push(...page.transportations);
      }
    });
    
    return allTransportations;
  } catch (error) {
    return [];
  }
}

/**
 * Utility function to log transportation coordinate state for debugging
 */
export function logTransportationCoordinatesState(transportation: TransportationInfo, stage: string) {
  // Função mantida para compatibilidade, mas sem logs
} 